import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, Thermometer, Shield, Leaf, Building } from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.15
    }
  }
};

const avantages = [
  "Traitement des ponts thermiques sans interruption de l'enveloppe",
  "Préservation de l'inertie thermique intérieure",
  "Amélioration du confort d'hiver comme d'été",
  "Sécurisation du point de rosée pour protéger la structure",
  "Prolongation de la durabilité du bâti existant",
  "Conservation de la surface habitable intérieure"
];

const supports = [
  { name: "Maçonnerie traditionnelle", icon: Building },
  { name: "Béton", icon: Building },
  { name: "Brique", icon: Building },
  { name: "Pierre", icon: Building },
  { name: "Ossature bois", icon: Building }
];

const criteres = [
  "Gestion de la migration de vapeur",
  "Compatibilité des matériaux",
  "Traitement des jonctions",
  "Continuité isolante sur toute l'enveloppe"
];

export const Isolation = () => {
  return (
    <div data-testid="isolation-page" className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="relative py-24 sm:py-32 lg:py-40 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1725862864518-1d95c2b32926"
            alt="Isolation thermique"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0908]/80 via-[#0a0908]/70 to-[#0a0908]"></div>
        </div>

        <motion.div 
          className="relative z-10 max-w-7xl mx-auto px-6 sm:px-12"
          initial="initial"
          animate="animate"
          variants={staggerContainer}
        >
          <motion.p 
            variants={fadeInUp}
            className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#C5A059] mb-6"
          >
            Isolation tout support
          </motion.p>
          
          <motion.h1 
            variants={fadeInUp}
            className="font-heading text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-light text-[#fdfbf7] mb-8 leading-tight max-w-4xl"
          >
            Votre isolation mérite mieux qu'une{' '}
            <span className="text-[#C5A059]">solution standard</span>
          </motion.h1>
          
          <motion.p 
            variants={fadeInUp}
            className="text-base sm:text-lg text-[#a39c93] max-w-2xl leading-relaxed font-body"
          >
            Elle mérite une stratégie. Chez Rozier Menuiserie, nous n'isolons pas uniquement un mur. 
            Nous optimisons un bâtiment dans son environnement.
          </motion.p>
        </motion.div>
      </section>

      {/* Introduction ITE */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="grid lg:grid-cols-2 gap-16 items-center"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Thermometer size={24} className="text-[#C5A059]" />
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059]">
                  Isolation Thermique par l'Extérieur
                </p>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Une stratégie thermique globale
              </h2>
              <p className="text-[#a39c93] leading-relaxed mb-6 font-body">
                L'isolation thermique par l'extérieur (ITE) est une stratégie thermique globale, 
                pensée en cohérence avec l'implantation, l'orientation et les usages de votre bâtiment.
              </p>
              <p className="text-[#a39c93] leading-relaxed font-body">
                Le choix du matériau isolant, notamment la fibre de bois lorsqu'elle est cohérente 
                avec le projet, est toujours la conséquence d'un audit climatique.
              </p>
            </div>
            
            <div className="bg-[#1c1a17] border border-[#2a2622] p-8 sm:p-10">
              <h3 className="font-heading text-xl text-[#fdfbf7] mb-6">
                Pourquoi isoler par l'extérieur ?
              </h3>
              <ul className="space-y-4">
                {avantages.map((avantage, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Check size={18} className="text-[#C5A059] mt-0.5 flex-shrink-0" />
                    <span className="text-[#a39c93] text-sm font-body">{avantage}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Supports */}
      <section className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059] mb-4">
              Approche sur mesure
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6">
              Nous intervenons sur tous types de supports
            </h2>
            <p className="text-[#a39c93] max-w-2xl mx-auto font-body">
              Chaque support nécessite une lecture spécifique avant toute intervention.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {supports.map((support, index) => (
              <motion.div
                key={support.name}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="bg-[#1c1a17] border border-[#2a2622] p-6 text-center hover:border-[#C5A059]/50 transition-colors duration-300"
              >
                <support.icon size={24} className="text-[#C5A059] mx-auto mb-3" />
                <p className="text-[#fdfbf7] text-sm font-body">{support.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Analyse Section */}
      <section className="py-24 sm:py-32 bg-[#0a0908]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image à gauche */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="order-2 lg:order-1"
            >
              <div className="relative">
                <div className="aspect-[4/5] overflow-hidden">
                  <img 
                    src="https://images.pexels.com/photos/6124239/pexels-photo-6124239.jpeg?auto=compress&cs=tinysrgb&w=800"
                    alt="Isolation thermique"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Encadré superposé */}
                <div className="absolute -bottom-8 -right-8 bg-[#C5A059] p-6 max-w-[200px]">
                  <p className="text-[#0a0908] font-heading text-lg leading-tight">
                    Une ITE réussie, c'est une ITE cohérente
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Texte à droite */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="order-1 lg:order-2"
            >
              <div className="flex items-center gap-3 mb-6">
                <Shield size={24} className="text-[#C5A059]" />
                <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059]">
                  Notre méthodologie
                </p>
              </div>
              <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6 leading-tight">
                Avant de choisir un système, nous analysons
              </h2>
              <ul className="space-y-4 mb-10">
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2"></div>
                  <span className="text-[#a39c93] font-body">Votre mur existant</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2"></div>
                  <span className="text-[#a39c93] font-body">Votre environnement climatique</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2"></div>
                  <span className="text-[#a39c93] font-body">Les différents usages de votre bâtiment</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 bg-[#C5A059] rounded-full mt-2"></div>
                  <span className="text-[#a39c93] font-body">Toutes ses contraintes hygrométriques</span>
                </li>
              </ul>
              
              <div className="border-l-2 border-[#C5A059] pl-6">
                <p className="text-[#a39c93] text-sm font-body mb-3">Nous veillons à :</p>
                <ul className="space-y-2">
                  {criteres.map((critere, index) => (
                    <li key={index} className="flex items-center gap-2 text-[#fdfbf7] text-sm font-body">
                      <Check size={14} className="text-[#C5A059]" />
                      {critere}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Applications */}
      <section className="py-24 sm:py-32 bg-[#0f0d0b]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Leaf size={24} className="text-[#C5A059]" />
              <p className="text-xs sm:text-sm uppercase tracking-[0.2em] text-[#C5A059]">
                Applications
              </p>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-light text-[#fdfbf7] mb-6">
              Pour quels bâtiments ?
            </h2>
            <p className="text-[#a39c93] max-w-3xl mx-auto font-body leading-relaxed">
              L'ITE s'applique aussi bien à votre maison individuelle, un bâtiment professionnel, 
              agricole, ou en rénovation lourde. Elle est particulièrement pertinente sur les bâtiments 
              existants présentant des déperditions importantes ou des ponts thermiques récurrents.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-[#1c1a17] border border-[#2a2622] overflow-hidden group hover:border-[#C5A059]/50 transition-colors duration-300">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1558661091-5cc1b64d0dc5?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Maison individuelle"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 text-center">
                <p className="text-[#fdfbf7] font-body">Maison individuelle</p>
              </div>
            </div>
            <div className="bg-[#1c1a17] border border-[#2a2622] overflow-hidden group hover:border-[#C5A059]/50 transition-colors duration-300">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.pexels.com/photos/269077/pexels-photo-269077.jpeg?auto=compress&cs=tinysrgb&w=800" 
                  alt="Bâtiment professionnel"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 text-center">
                <p className="text-[#fdfbf7] font-body">Bâtiment professionnel</p>
              </div>
            </div>
            <div className="bg-[#1c1a17] border border-[#2a2622] overflow-hidden group hover:border-[#C5A059]/50 transition-colors duration-300">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1773416189040-f8b5df4ecab6?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Bâtiment agricole"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 text-center">
                <p className="text-[#fdfbf7] font-body">Bâtiment agricole</p>
              </div>
            </div>
            <div className="bg-[#1c1a17] border border-[#2a2622] overflow-hidden group hover:border-[#C5A059]/50 transition-colors duration-300">
              <div className="aspect-[4/3] overflow-hidden">
                <img 
                  src="https://images.unsplash.com/photo-1768321901750-f7b96d774456?crop=entropy&cs=srgb&fm=jpg&q=85" 
                  alt="Rénovation lourde"
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>
              <div className="p-5 text-center">
                <p className="text-[#fdfbf7] font-body">Rénovation lourde</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 sm:py-32 bg-[#1c1a17]">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-[#fdfbf7] mb-6">
              Votre bâtiment mérite une enveloppe<br />
              <span className="text-[#C5A059]">performante et durable</span>
            </h2>
            <p className="text-[#a39c93] max-w-2xl mx-auto mb-8 font-body">
              Vous avez un projet d'isolation ou souhaitez améliorer le confort thermique de votre bâtiment ? 
              Parlons-en et posons ensemble les bases d'une enveloppe cohérente et efficace dans la durée.
            </p>
            <Link
              to="/contact"
              data-testid="isolation-cta-contact"
              className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0a0908] px-8 py-4 text-sm uppercase tracking-[0.15em] hover:bg-[#d6b46e] transition-colors duration-300"
            >
              Contactez-nous
              <ArrowRight size={16} />
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
